import { useForm } from "react-hook-form"
import { toast } from "sonner"
import type { AxiosError } from "axios"
import { Loader2 } from "lucide-react"
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@components/ui/form"
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@components/ui/select"
import { Button } from "@components/ui/button"
import { Input } from "@components/ui/input"
import {
  useAddTraitMutation,
  useGetAllTraitsQuery,
} from "@apis/traits/useTraits"
import { useAuthUserDetails } from "@apis/users/useUsers"

type TAddMemberTraitsFormProps = {
  prospect_guid: string
}

type TTraitFormData = {
  trait_code: string
  reason?: string
}

export const AddMemberTraitsForm = ({
  prospect_guid,
}: TAddMemberTraitsFormProps) => {
  const form = useForm<TTraitFormData>({
    mode: "onTouched",
  })
  const authUserDetails = useAuthUserDetails()
  const getAllTraits = useGetAllTraitsQuery()
  const addTraitMutation = useAddTraitMutation()

  if (getAllTraits.isPending || authUserDetails.isPending) {
    return (
      <Loader2 className="tw:h-6 tw:w-6 tw:animate-spin tw:text-gray-500" />
    )
  }

  if (getAllTraits.isError) {
    return (
      <div className="tw:text-red-500">
        There was an error loading Add Traits. Error:{" "}
        {getAllTraits.error.message}
      </div>
    )
  }

  function onSubmit(data: TTraitFormData) {
    addTraitMutation.mutate(
      {
        prospect_guid: prospect_guid,
        trait_code: data.trait_code,
        reason: data.reason,
        user_id: authUserDetails.data?.user_id,
      },
      {
        onSuccess: () => {
          toast.success("Trait Added!")
        },
        onError: (error: string | AxiosError) =>
          toast.error(`Error Adding Trait! ${error}`),
      },
    )
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="tw:space-y-6">
        <div className="tw:grid tw:auto-cols-min tw:grid-cols-3 tw:items-end tw:justify-start tw:gap-4">
          <FormField
            control={form.control}
            name="trait_code"
            rules={{ required: "Trait is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Trait</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl className="tw:w-full">
                    <SelectTrigger>
                      <SelectValue placeholder="Select Trait" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {getAllTraits.data?.map(
                      (option) =>
                        option.lucide_trait_icon && (
                          <SelectItem
                            key={option.trait_code}
                            value={option.trait_code}
                          >
                            {option.trait_name}
                          </SelectItem>
                        ),
                    )}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="reason"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Reason</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Reason"
                    value={field.value ?? ""}
                    onChange={(e) => {
                      const val = e.target.value
                      field.onChange(val === "" ? undefined : val)
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button disabled={!form.formState.isValid} type="submit">
            Add Trait
          </Button>
        </div>
      </form>
    </Form>
  )
}
